import { useEffect, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import Card from '../../Card/Card';
import RadioGroup from '../../FormElements/RadioGroup/RadioGroup';
import TabActionButtons from '../../FormElements/TabActionButtons/TabActionButtons';
import ToggleSwitch from '../../FormElements/ToggleSwitch/ToggleSwitch';
import styles from './TabSections.module.css';

const GeneralSettings = ({ onSave, onReset, settingsData, onSettingsChange, showToast }) => {
  const [settings, setSettings] = useState({
    hvnly_analyticsEnabled: true,
    hvnly_userTrackingEnabled: false,
    hvnly_trackingMode: 'basic',
    hvnly_defaultUserRole: 'Administrator',
    hvnly_adminAccess: true,
    hvnly_editorAccess: false
  });

  useEffect(() => {
    if (settingsData && Object.keys(settingsData).length > 0) {
      setSettings(prev => ({ ...prev, ...settingsData }));
    }
  }, [settingsData]);

  const handleInputChange = (field, value) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    onSettingsChange?.(newSettings);
  };

  const handleSave = async () => {
    if (onSave) {
      await onSave(settings);
      return true;
    }
    return false;
  };

  const handleReset = async () => {
    const defaultSettings = {
      hvnly_analyticsEnabled: true,
      hvnly_userTrackingEnabled: false,
      hvnly_trackingMode: 'basic',
      hvnly_defaultUserRole: 'Administrator',
      hvnly_adminAccess: true,
      hvnly_editorAccess: false
    };
    setSettings(defaultSettings);
    onSettingsChange?.(defaultSettings);
    if (onReset) {
      await onReset();
      return true;
    }
    return false;
  };

  return (
    <div className={`${styles['hvnly-settings-api__content']} ${styles['hvnly-settings-api__content--active']}`} id="general">
      <h2>{__('General Settings', 'havenlytics')}</h2>
      
      <Card title={__("Plugin Activation", "havenlytics")} icon="fas fa-toggle-on" collapsible>
        <div className={styles['hvnly-settings-api__form-group']}>
          <ToggleSwitch
            id="hvnly_analyticsEnabled"
            checked={settings.hvnly_analyticsEnabled}
            onChange={(e) => handleInputChange('hvnly_analyticsEnabled', e.target.checked)}
            label={__("Enable Havenlytics Analytics", "havenlytics")}
          />
          
          <ToggleSwitch
            id="hvnly_userTrackingEnabled"
            checked={settings.hvnly_userTrackingEnabled}
            onChange={(e) => handleInputChange('hvnly_userTrackingEnabled', e.target.checked)}
            label={__("Enable User Tracking", "havenlytics")}
          />
        </div>

        <div className={styles['hvnly-settings-api__form-group']}>
          <label className={styles['hvnly-settings-api__form-label']}>{__("Tracking Mode", "havenlytics")}</label>
          <RadioGroup
            name="hvnly_trackingMode"
            options={[
              { value: 'basic', label: __('Basic Tracking', 'havenlytics') },
              { value: 'enhanced', label: __('Enhanced Tracking', 'havenlytics') },
              { value: 'advanced', label: __('Advanced Tracking', 'havenlytics') }
            ]}
            selectedValue={settings.hvnly_trackingMode}
            onChange={(value) => handleInputChange('hvnly_trackingMode', value)}
          />
        </div>
      </Card>
      
      <Card title={__("User Settings", "havenlytics")} icon="fas fa-user" collapsible>
        <div className={styles['hvnly-settings-api__form-group']}>
          <label className={styles['hvnly-settings-api__form-label']}>{__("Default User Role", "havenlytics")}</label>
          <input 
            type="text" 
            className={styles['hvnly-settings-api__form-control']} 
            value={settings.hvnly_defaultUserRole}
            onChange={(e) => handleInputChange('hvnly_defaultUserRole', e.target.value)}
          />
        </div>

        <div className={styles['hvnly-settings-api__form-group']}>
          <label className={styles['hvnly-settings-api__form-label']}>{__("Access Control", "havenlytics")}</label>
          <ToggleSwitch
            id="hvnly_adminAccess"
            checked={settings.hvnly_adminAccess}
            onChange={(e) => handleInputChange('hvnly_adminAccess', e.target.checked)}
            label={__("Allow access to Administrators", "havenlytics")}
          />
          
          <ToggleSwitch
            id="hvnly_editorAccess"
            checked={settings.hvnly_editorAccess}
            onChange={(e) => handleInputChange('hvnly_editorAccess', e.target.checked)}
            label={__("Allow access to Editors", "havenlytics")}
          />
        </div>
      </Card>
      
      <TabActionButtons
        onSave={handleSave}
        onReset={handleReset}
        showToast={showToast}
        saveLabel={__("Save General Settings", "havenlytics")}
        resetLabel={__("Reset Changes", "havenlytics")}
      />
    </div>
  );
};

export default GeneralSettings;